import { MutableRefObject, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { NetworkPerformance } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { TaskSource } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/enums.pb';
import { ProductTier } from '@weave/schema-gen-ts/dist/shared/call-intelligence/plg/product.pb';
import { AIOutcome, CallStatus } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/enums.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { Entity } from '@weave/schema-gen-ts/dist/shared/wsearch/v1/entity.pb';
import { UiSource } from '@weave/schema-gen-ts/dist/shared/wsearch/v1/ui_source.pb';
import { CustomerLifecycleManagementOrg } from '@wetags/customer-lifecycle-management';
import { PhoneEventsOrg } from '@wetags/phone-events';
import dayjs from 'dayjs';
import { produce } from 'immer';
import { omit, startCase } from 'lodash-es';
import { InfiniteData, useQueryClient, useQueries, UseInfiniteQueryResult } from 'react-query';
import { Row } from 'react-table';
import { CallIntelMocks } from '@frontend/api-call-intel';
import { CallRecordingApi } from '@frontend/api-call-recordings';
import { DepartmentsApi, DepartmentsTypes } from '@frontend/api-departments';
import { PhoneCallsApi } from '@frontend/api-phone-calls';
import { PhoneNumbersV1 } from '@frontend/api-phone-numbers';
import { TagsUtils, TagsV2 } from '@frontend/api-tag';
import { AdaptoActions } from '@frontend/adapto-actions';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { PanelEvents, PhoneTreePreview, ScreenType, useCallInsightController } from '@frontend/call-insight-panel';
import {
  CategoryColumn,
  ContactTypeColumn,
  EmptyCell,
  SchedulingOutcomeColumn,
  SentimentColumn,
  ServiceIssueColumn,
  TaskColumn,
  useCallIntelColumnsAccess,
  useCallIntelWeaveDemoUser,
  canUseCallIntelPremiumFeatures,
  hasCallIntelPlanAccess,
} from '@frontend/call-intel-core';
import { CallIntelBaseAnnouncementBanner, useCallInsightEligibleHotspot } from '@frontend/call-intel-onboarding';
import { Chips } from '@frontend/chips';
import { EmptyStateConfig } from '@frontend/components';
import appConfig from '@frontend/env';
import { useGetDisplayName } from '@frontend/get-display-name';
import { useTranslation, i18next } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumber, isPartialPhoneNumber, isPhoneNumber, sanitizePhoneNumber } from '@frontend/phone-numbers';
import {
  useRecentCallsTagMutation,
  optimisticApplyTag as optimisticCallRecordsApplyTag,
  optimisticRemoveTag as optimisticCallRecordsRemoveTag,
  RecentCallRecord,
  RecentCallsTagSelector,
  AddTag,
  RecentCallRecords,
  PhonesUtils,
  CallDetailsContext,
  CachedAudioScrubber,
} from '@frontend/phone-panels';
import { AiOfficeUser, AiOfficeUserRouted, CreditUsageLabel, WithTooltip } from '@frontend/phone-ui';
import { AiEventDescriptionModalTrigger, AiEventInfoTableCell } from '@frontend/phone-ui-widgets';
import { OnboardingHotspot, useOnboardingShallowStore, HOTSPOT_TARGET } from '@frontend/product-onboarding';
import { useMutation } from '@frontend/react-query-helpers';
import { useTargetElementContainerQuery, breakpoints } from '@frontend/responsiveness';
import { PhoneRecordsSchemaService } from '@frontend/schema';
import { useAppScopeStore, useOnScopeChange, useScopedAppFlagStore, useScopedInfiniteQuery } from '@frontend/scope';
import {
  formatResultsPrimaryKeys,
  handleRegisterClick,
  handleTriggerSearch,
  handleUpdateSearch,
  SearchHistory,
  useSearchHistoryShallowStore,
} from '@frontend/search-history';
import { useVoiceAIRCustomizationFlag } from '@frontend/shared';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { useTaskCitation } from '@frontend/task-center-scope';
import { useSearch, useNavigate, type AnySearch } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import {
  Table,
  Chip,
  useAlert,
  useModalControl,
  ConfirmationModal,
  DownloadIcon,
  TableInstance,
  TextLink,
  useDebouncedFn,
  Text,
  Truncated,
  TableColumnConfig,
  SpinningLoader,
  Modal,
} from '@frontend/design-system';
import { useCallHistoryCallInsight } from '../../hooks/use-call-history-call-insight';
import {
  AllCallsTable,
  CallRecordsFiltersType,
  PaginationSettings,
  usePhonePageSettingShallowStore,
} from '../../hooks/use-phone-config-state-store';
import { queryKeys } from '../../query-keys';
import { useRecentCallsRealtimeShallowStore } from '../../stores/recent-calls-realtime-store';
import { useVoicemailDetailsShallowStore } from '../../stores/voicemail-details-store';
import { formatEndDateWithTimeZone, formatStartDateWithTimeZone } from '../../utils';
import { ProfileCard } from '../all-calls/profile-card';
import { ToolbarChipFilter } from '../all-calls/toolbar-filter';
import { HydratedCallRecord } from '../all-calls/types';
import { applyCallIntelInsightUpdate, applyRecentCallTagsUpdate } from './call-record-cache.helpers';
import { RecentCallsTrayFilter } from './calls-filter';
import { CallStatusTabs } from './constants';
import { HistoryActionsGetterProps, useHistoryActionsGetter } from './history-actions';
import { HistoryExportModal } from './history-export.modal';
import { SmallView } from './history-mobile-view-components';
import { AiCallCreditInfoModalTrigger, AiOutcomeInfoModalTrigger, ResultInfoModalTrigger } from './info-popover';
import {
  createDefaultRecentCallsFilters,
  DEFAULT_RECENT_CALLS_FILTERS,
  hasRecentCallsFiltersChanged,
} from './recent-calls-filters';
import { getAiOutcomeIcon } from './ui-utils';
import { getRecentCallsSearchQuery } from './utils';
import { ViewCallDetailsColumn } from './view-call-details';
import { VoicemailButton } from './voicemail-button';

const defaultRecentCallsFilters = createDefaultRecentCallsFilters();

type PageParams = {
  lastId: string;
  endDate: string;
  hasNext: boolean;
};

type SetPageConfigInput = Partial<PaginationSettings> | ((_: PaginationSettings) => Partial<PaginationSettings>);

const fetchDepartmentsByLocation = async (locationId: string) => {
  const response = await DepartmentsApi.listDept({ locationId }, { locationId: locationId });
  return response.departments;
};

const defaultEmptyState: EmptyStateConfig = {
  type: 'sync_your_phone',
  header: i18next.t('No Items to Display'),
  description: i18next.t('Inbound and outbound call records will display here.'),
};

const entity = Entity.ENTITY_CALL;

export const RecentCallsTable = () => {
  const alert = useAlert();
  const { t } = useTranslation('calls');
  const search = useSearch({ strict: false });
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const showEnabledFiltersBadgeHandler = useRef<((showNotification: boolean) => void) | undefined>(undefined);

  const { locationData, getLocationName, selectedOrgId, selectedLocationIds, accessibleLocationData } =
    useAppScopeStore();

  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const departmentsEnabled = getFeatureFlagValue('departments');
  const { isActive: hasVoiceAi } = useVoiceAIRCustomizationFlag(selectedLocationIds);
  const [isAirEventsUiEnabled] = PhoneEventsOrg.useWetag('fe-air-events-ui');

  const {
    showCallIntelInsights,
    showServiceQuality,
    hasCallIntelPremiumFeatures,
    currentPlan,
    isDemoUser: isWeaveDemoUser,
    locationPlanMap: callIntelLocationPlanMap,
  } = useCallIntelColumnsAccess({
    locationIds: selectedLocationIds,
  });

  const { context } = useSlidePanelShallowStore<CallDetailsContext>('context');

  const {
    config,
    setPageSize,
    setPageNumber,
    setFilters: setCallsPageFilters,
  } = usePhonePageSettingShallowStore('config', 'setPageSize', 'setPageNumber', 'setFilters');

  const { onEvent, offEvent } = useCallInsightController({
    source: 'callHistory',
  });
  const { registerConsumer, unregisterConsumer } = useRecentCallsRealtimeShallowStore(
    'registerConsumer',
    'unregisterConsumer'
  );

  CallIntelMocks.useCallInsightPanelDemoInterceptor({
    enabled: isWeaveDemoUser,
  });

  const [emptyState, setEmptyState] = useState<EmptyStateConfig>(defaultEmptyState);
  const [editedCallIds, setEditedCallIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('all-calls');
  const pageNumber = config[AllCallsTable.RecentCalls].pageNumber;
  const pageSize = config[AllCallsTable.RecentCalls].pageSize;
  const filtersInStore = config[AllCallsTable.RecentCalls].filters;
  const filters = filtersInStore || defaultRecentCallsFilters;

  const onTabChange = (tabId: string) => {
    setActiveTab(tabId);
    setFilters({
      ...filters,
      status:
        tabId === CallStatusTabs.MISSED_AND_ABANDONED_CALLS
          ? [CallStatus.CALL_STATUS_MISSED, CallStatus.CALL_STATUS_ABANDONED]
          : [],
    });
  };

  const setPageConfig = useCallback(
    (input: SetPageConfigInput) => {
      const currentPageNumber = config[AllCallsTable.RecentCalls].pageNumber;
      const currentPageSize = config[AllCallsTable.RecentCalls].pageSize;
      const data =
        typeof input === 'function' ? input({ pageSize: currentPageSize, pageNumber: currentPageNumber }) : input;
      if (data.pageSize) setPageSize(AllCallsTable.RecentCalls, data.pageSize);
      if (data.pageNumber) setPageNumber(AllCallsTable.RecentCalls, data.pageNumber);
    },
    [config, setPageSize, setPageNumber]
  );

  const setFilters = useCallback(
    (nextFilters: CallRecordsFiltersType) => {
      setCallsPageFilters(AllCallsTable.RecentCalls, nextFilters);
      if (hasRecentCallsFiltersChanged(nextFilters, DEFAULT_RECENT_CALLS_FILTERS)) {
        showEnabledFiltersBadgeHandler.current?.(true);
      } else {
        showEnabledFiltersBadgeHandler.current?.(false);
      }
      setPageConfig({ pageNumber: 1 });
    },
    [setCallsPageFilters, setPageConfig]
  );

  const getPhoneFullAccessAcl = useCallback((candidateLocation: any) => {
    return candidateLocation?.locationType === 'unify'
      ? hasSchemaACL(candidateLocation.groupId, Permission.PHONE_FULL_ACCESS)
      : hasSchemaACL(candidateLocation.id, Permission.PHONE_FULL_ACCESS);
  }, []);

  const { hasAccesstoCallRecordingMap, hasDeleteAccesstoCallRecordingMap } = useMemo(() => {
    const readAccessMap: Record<string, boolean> = {};
    const deleteAccessMap: Record<string, boolean> = {};

    selectedLocationIds.forEach((locationId) => {
      const candidateLocation = accessibleLocationData[locationId];
      if (!candidateLocation) {
        readAccessMap[locationId] = false;
        deleteAccessMap[locationId] = false;
        return;
      }

      const hasPhoneFullAccessAcl = getPhoneFullAccessAcl(candidateLocation);

      readAccessMap[locationId] = hasSchemaACL(locationId, Permission.CALL_RECORDING_READ) || hasPhoneFullAccessAcl;
      deleteAccessMap[locationId] = hasSchemaACL(locationId, Permission.CALL_RECORDING_DELETE) || hasPhoneFullAccessAcl;
    });

    return {
      hasAccesstoCallRecordingMap: readAccessMap,
      hasDeleteAccesstoCallRecordingMap: deleteAccessMap,
    };
  }, [selectedLocationIds, accessibleLocationData, getPhoneFullAccessAcl]);

  const hasAccesstoCallRecording = useMemo(() => {
    return Object.values(hasAccesstoCallRecordingMap).some((hasAccess) => hasAccess);
  }, [hasAccesstoCallRecordingMap]);

  const departmentQueries = useQueries(
    selectedLocationIds.map((locationId) => ({
      queryKey: ['departments', locationId],
      queryFn: () => fetchDepartmentsByLocation(locationId),
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      retry: 2,
      onError: (error: Error) => {
        console.error(`Failed to fetch departments for location ${locationId}:`, error);
      },
    }))
  );

  const departmentsByLocation = useMemo(() => {
    const result: Record<string, DepartmentsTypes.ListDepartmentTypes['output']> = {};

    departmentQueries.forEach((query, index) => {
      const locationId = selectedLocationIds[index];
      if (query.isSuccess && query.data) {
        result[locationId] = { departments: query.data };
      } else if (query.isError) {
        // Provide empty departments array as fallback
        result[locationId] = { departments: [] };
      }
    });

    return result;
  }, [departmentQueries, selectedLocationIds]);

  useOnScopeChange(() => {
    setFilters(createDefaultRecentCallsFilters({ locationIds: [] }));
  });

  useEffect(() => {
    // initialize the filters if empty
    if (!filtersInStore) {
      // set the filter state in store which is used to fetch the data
      // in call record realtime update
      setFilters(filters);
    }
  }, [filters, filtersInStore, setFilters]);

  const queryFilters = search?.filters;

  useEffect(() => {
    if (queryFilters) {
      try {
        const decodedFilters = atob(queryFilters as string);
        const appliedFilters = JSON.parse(decodedFilters) as Partial<CallRecordsFiltersType>;

        const newFilters = createDefaultRecentCallsFilters(appliedFilters);
        setFilters(newFilters);

        navigate({ search: omit(search, ['filters']) as AnySearch, replace: true });
      } catch (error) {
        console.error('Failed to process filters:', error);
      }
    }
  }, [navigate, queryFilters, search, setFilters]);

  const { data: mappedTags = {}, isLoading: isTagDataLoading } = TagsV2.Queries.useListTagsQuery({
    request: { orgId: selectedOrgId, groupIds: selectedLocationIds },
    options: {
      select: (data) => {
        return data?.tags.reduce<Record<string, Tag>>((acc, tag) => {
          acc[tag.id] = tag;
          return acc;
        }, {});
      },
    },
  });

  const searchQuery = useMemo(
    () => getRecentCallsSearchQuery(filters, pageSize, mappedTags),
    [filters, pageSize, mappedTags]
  );
  const { searchId, setDebouncedValue } = useSearchHistoryShallowStore('searchId', 'setDebouncedValue');
  const onSearchHistoryChange = useDebouncedFn((value) => setDebouncedValue(value), 3000);

  useEffect(() => {
    handleTriggerSearch({
      entities: [entity],
      searchTerm: filters.phoneNumber || filters.name || '',
      uiSource: UiSource.RECENT_CALLS_SEARCH,
    });
  }, [filters.name, filters.phoneNumber]);

  const { data } = PhoneNumbersV1.Queries.useListPhoneNumbersForLocationsQuery({
    request: { locationIds: selectedLocationIds, voice: true },
  });

  const phoneNumberOptions = useMemo(() => {
    return data?.phoneNumbers?.reduce((acc, { locationId, phoneNumber, displayName }) => {
      if (locationId && selectedLocationIds.includes(locationId)) {
        const key = locationId;
        if (acc[key]) {
          acc[key].numbers.push({
            value: phoneNumber.nationalNumber + '',
            label: formatPhoneNumber(phoneNumber.nationalNumber + ''),
            name: displayName || '',
          });
        } else {
          acc[key] = {
            name: getLocationName(locationId),
            numbers: [
              {
                value: phoneNumber.nationalNumber + '',
                label: formatPhoneNumber(phoneNumber.nationalNumber + ''),
                name: displayName || '',
              },
            ],
          };
        }
      }
      return acc;
    }, {} as Record<string, { name: string; numbers: { value: string; label: string; name: string }[] }>);
  }, [data?.phoneNumbers, getLocationName, selectedLocationIds]);

  const infiniteQueryProps = useScopedInfiniteQuery<RecentCallRecords, unknown>({
    queryKey: queryKeys.recentCallLogs(searchQuery),
    queryFn: async ({ pageParam }) => {
      const CANARY = 1;
      const { records = [] } = await PhoneCallsApi.getMultiCallRecords({
        limit: pageSize + CANARY,
        locationIds: filters.locationIds.length ? filters.locationIds : selectedLocationIds,
        filters: {
          startTime: formatStartDateWithTimeZone(filters.startDate),
          endTime: formatEndDateWithTimeZone(filters.endDate),
          callStatus: filters.status,
          callDirection: filters.callDirection,
          phoneNumber: filters.phoneNumber,
          name: filters.name,
          officePhoneNumbers: filters.officePhoneNumbers,
          aiOutcomes: isAirEventsUiEnabled ? undefined : filters.aiOutcomes,
          aiReceptionistEventTypes: isAirEventsUiEnabled ? filters.aiEvents : undefined,
          aiReceptionistHandled: filters.aiReceptionistHandled,
          handledByUserIds: filters.handledByUserIds,
          allOfficeUsers: filters.allOfficeUsers,
        },
        page: {
          lastRecordId: pageParam?.lastId,
          lastRecordTime: pageParam?.endDate,
        },
        includeCallIntelligence: showCallIntelInsights,
        includeAiCallCreditsDetail: hasVoiceAi,
        isDemo: isWeaveDemoUser,
        demoMetadata: {
          vertical: locationData[selectedLocationIds[0]]?.vertical,
        },
        ...(showCallIntelInsights && {
          callIntelMetadata: {
            subscriptionPlan: currentPlan,
          },
        }),
        includeDownloadCallRecordingToken: true,
      });
      const parsedData: RecentCallRecord[] = records.map((callItem: HydratedCallRecord) => {
        return { ...callItem, tags: callItem.tagIds?.map((tagId) => mappedTags[tagId]).filter(Boolean) || [] };
      });

      const lastRowIndex = pageSize + CANARY > records.length ? records.length - 1 : records.length - 1 - CANARY;
      const lastRowData = parsedData[lastRowIndex];

      const actualSlice = parsedData.slice(0, pageSize);

      return {
        data: [...actualSlice],
        rows: [...actualSlice],
        meta: lastRowData
          ? { lastId: lastRowData.id, endDate: lastRowData.startedAt, hasNext: parsedData.length > pageSize }
          : {
              lastId: undefined,
              endDate: undefined,
              hasNext: false,
            },
      };
    },
    getNextPageParam: (lastPage) => {
      return lastPage.meta?.hasNext ? (lastPage.meta as PageParams) : false;
    },
    onError: () => {
      alert.error(t("Couldn't load the data. Please try again."));
    },
    onSettled: (res) => {
      // send the results
      handleUpdateSearch({
        entity,
        results: formatResultsPrimaryKeys<RecentCallRecord>('id', res?.pages[0]?.data),
      });
    },
    enabled: !isTagDataLoading,
  });

  const downloadMedia = async (record: HydratedCallRecord) => {
    const mediaUrl = `${appConfig.BACKEND_API}/portal/v1/phone/callRecordings/${record.channelId}?location_id=${record.locationId}`;
    try {
      const blob: any = await CallRecordingApi.download(mediaUrl);
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${url.split('/')[3]}.mp3`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (e) {
      alert.error(t('unable to download the media'));
    }
  };

  const downloadModalControls = useModalControl();

  const handleExport = () => {
    downloadModalControls.triggerProps.onClick();
  };

  const deleteModalControls = useModalControl();
  const deletionCandidate = useRef<HydratedCallRecord | null>(null);

  const { mutate: deleteMedia } = useMutation(
    async (media: HydratedCallRecord) => {
      if (media.channelId && media.locationId) {
        return PhoneRecordsSchemaService.DeleteCallRecordings({
          channelIds: [media.channelId],
          locationIds: [media.locationId],
        });
      }

      return Promise.reject(new Error('Invalid media data'));
    },
    {
      onMutate: (media) => {
        //optimistic update
        const queryKey = [selectedLocationIds, ...queryKeys.recentCallLogs(searchQuery)];
        // get snapshot
        const previousData = queryClient.getQueryData(queryKey);

        queryClient.setQueryData(queryKey, (oldData: any) => {
          const res = produce(oldData, (draft: { pages: { data: any[] }[] }) => {
            if (!draft) return draft;

            const item = draft.pages[pageNumber - 1]?.data.find((_item: any) => {
              return _item.channelId === media.channelId;
            });

            if (item) {
              item.recordingPath = null;
              item.recordingFilename = null;
              item.downloadCallRecordingToken = '';
            }
          });
          return res;
        });

        return { previousData };
      },
      onError: (_err, _newData, old) => {
        alert.error(t('Media deletion failed.'));
        const queryKey = [selectedLocationIds, ...queryKeys.recentCallLogs(searchQuery)];

        queryClient.setQueryData(queryKey, old?.previousData);
        queryClient.setQueryData(queryKey, old?.previousData);
      },
      onSuccess: (_, req) => {
        queryClient.removeQueries([selectedLocationIds, 'phone-media', `call-recording-${req.channelId}`]);
        alert.success(t('Call Recording successfully deleted.'));
      },
      onSettled: () => {
        deletionCandidate.current = null;
      },
    }
  );

  const hasFilterChanged = (
    incomingFilters: CallRecordsFiltersType,
    baselineFilters: CallRecordsFiltersType = filters
  ): boolean => {
    return hasRecentCallsFiltersChanged(incomingFilters, baselineFilters);
  };

  const shouldRegisterSearch = Boolean(searchId && (filters.name || filters.phoneNumber));

  const getHistoryActions = useHistoryActionsGetter({
    deleteMedia: (rowData) => {
      deletionCandidate.current = rowData;
      deleteModalControls.openModal();
    },
    downloadMedia,
    deleteAccessMap: hasDeleteAccesstoCallRecordingMap,
    downloadAccessMap: hasAccesstoCallRecordingMap,
    searchQuery,
    onAfterAction: shouldRegisterSearch ? registerSearch : undefined,
  });

  const searchHandler = (searchText: string) => {
    const sanitizedNumber = sanitizePhoneNumber(searchText);
    const numberSearch = isPhoneNumber(sanitizedNumber) || isPartialPhoneNumber(sanitizedNumber) ? sanitizedNumber : '';
    const nameSearch = !isPhoneNumber(sanitizedNumber) && !isPartialPhoneNumber(sanitizedNumber) ? searchText : '';

    if (!!isPartialPhoneNumber(searchText)) {
      setEmptyState({
        type: 'sync_your_phone',
        header: t('Phone Number Searches Must be Exactly 10 Digits'),
        description: t('Phone number searches must be exactly 10 digits in order to return a match.'),
      });
    } else {
      setEmptyState(defaultEmptyState);
    }

    setFilters({
      ...filters,
      phoneNumber: numberSearch,
      name: nameSearch,
    });
    onSearchHistoryChange(searchText);
  };

  const [isMatch] = useTargetElementContainerQuery({
    maxWidth: breakpoints.xsmall.max,
    element: document.querySelector('#main-content') as HTMLElement,
  });

  const handleUpdateCallAnalysis = useCallback(
    ({ call }: PanelEvents['callAnalysisUpdated']) => {
      const queryKey = [selectedLocationIds, ...queryKeys.recentCallLogs(searchQuery)];

      queryClient.setQueryData<InfiniteData<RecentCallRecords> | undefined>(queryKey, (oldData) => {
        return applyCallIntelInsightUpdate(oldData, call);
      });

      setEditedCallIds((prevIds) => {
        const updatedIds = new Set(prevIds);
        call.id && updatedIds.add(call.id);
        return Array.from(updatedIds);
      });
    },
    [queryClient, searchQuery, selectedLocationIds]
  );

  const handleUpdateCallTags = useCallback(
    ({ callRecord, tagIds }: PanelEvents['callTagsUpdated']) => {
      if (!callRecord.channelId) {
        return;
      }

      const channelId = callRecord.channelId;

      const queryKey = [selectedLocationIds, ...queryKeys.recentCallLogs(searchQuery)];
      queryClient.setQueryData<InfiniteData<RecentCallRecords> | undefined>(queryKey, (oldData) => {
        return applyRecentCallTagsUpdate({
          callRecords: oldData,
          channelId,
          tagIds,
          mappedTags,
        });
      });
    },
    [mappedTags, queryClient, searchQuery, selectedLocationIds]
  );

  useEffect(() => {
    onEvent('callAnalysisUpdated', handleUpdateCallAnalysis);
    onEvent('callTagsUpdated', handleUpdateCallTags);
    return () => {
      offEvent('callAnalysisUpdated', handleUpdateCallAnalysis);
      offEvent('callTagsUpdated', handleUpdateCallTags);
    };
  }, [onEvent, offEvent, handleUpdateCallAnalysis, handleUpdateCallTags]);

  useEffect(() => {
    registerConsumer();

    return () => {
      unregisterConsumer();
    };
  }, [registerConsumer, unregisterConsumer]);

  return (
    <div css={styles.wrapper}>
      <CallIntelBaseAnnouncementBanner containerStyles={styles.announcementBannerWrapper} />
      {!isMatch ? (
        <CallHistoryTableView
          hasVoiceAi={hasVoiceAi}
          isAirEventsUiEnabled={isAirEventsUiEnabled}
          isLoading={infiniteQueryProps.isFetching || infiniteQueryProps.isFetchingNextPage}
          getHistoryActions={getHistoryActions}
          infiniteQueryProps={infiniteQueryProps}
          selectedLocationIds={selectedLocationIds}
          emptyState={emptyState}
          panelProps={{
            callDetailsQueryKey: queryKeys.recentCallLogs(searchQuery).filter(Boolean),
            hasAccessToRecordings: hasAccesstoCallRecording,
            sectionFocus: context?.sectionFocus || 'details',
          }}
          searchQuery={searchQuery}
          setPageConfig={setPageConfig}
          pageNumber={pageNumber}
          pageSize={pageSize}
          filterConfig={{
            setFilters,
            filters,
            defaultFilters: defaultRecentCallsFilters,
            phoneNumberOptions: phoneNumberOptions || {},
            searchHandler,
            hasFilterChanged,
            showEnabledFiltersBadgeHandler,
          }}
          accessConfig={{
            callIntelLocationPlanMap,
            hasAccesstoCallRecording,
            hasAccesstoCallRecordingMap,
            departmentsEnabled,
            showCallIntelInsights,
            hasCallIntelPremiumFeatures,
            showServiceQuality,
          }}
          dataConfig={{
            departmentsByLocation,
            editedCallIds,
            shouldRegisterSearch,
          }}
          handlers={{
            handleExport,
          }}
        />
      ) : (
        <SmallView
          hasVoiceAi={hasVoiceAi}
          showCallIntelInsights={showCallIntelInsights}
          getHistoryActions={getHistoryActions}
          selectedLocationIds={selectedLocationIds}
          infiniteQueryProps={infiniteQueryProps}
          emptyState={emptyState}
          panelProps={{
            callDetailsQueryKey: queryKeys.recentCallLogs(searchQuery).filter(Boolean),
            hasAccessToRecordings: hasAccesstoCallRecording,
            sectionFocus: context?.sectionFocus || 'details',
          }}
          activeTab={activeTab}
          onTabChange={onTabChange}
        />
      )}
      <ConfirmationModal
        {...deleteModalControls.modalProps}
        title={t('Delete Call Recording?')}
        message={t('Are you sure you want to delete this Call Recording?')}
        destructive
        cancelLabel={t('No, Cancel')}
        confirmLabel={t('Yes, Delete')}
        maxWidth={600}
        modalCss={css`
          min-height: 208px;
        `}
        onConfirm={() => {
          if (deletionCandidate.current) {
            deleteMedia(deletionCandidate.current);
          }
        }}
      />
      <Modal {...downloadModalControls.modalProps} maxWidth={600}>
        <HistoryExportModal onClose={downloadModalControls.modalProps.onClose} />
      </Modal>
      <SearchHistory id={UiSource.RECENT_CALLS_SEARCH} />
    </div>
  );
};

const styles = {
  wrapper: css`
    flex: 1;
    max-width: 100%;
    height: 100%;
    overflow: hidden;
    padding-top: ${theme.spacing(1)};
    position: relative;

    @media (max-width: ${breakpoints.small.max}px) {
      padding-top: 0;
    }
  `,

  filtersWrapper: css`
    order: -1;
    margin-right: ${theme.spacing(2)};

    @media screen and (max-width: 1500px) {
      flex: 1;
    }
  `,

  audio: css`
    height: ${theme.spacing(4)};
  `,

  noRecordingText: css`
    color: ${theme.colors.neutral90};
    display: inline-block;
    text-align: left;
    width: 100%;
  `,
  recordingBeingProcessed: css`
    animation: fade 1.5s infinite ease-in-out;

    @keyframes fade {
      0%,
      100% {
        opacity: 1;
      }
      50% {
        opacity: 0.5;
      }
    }
  `,
  tagsWrapper: css`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
  `,

  announcementBannerWrapper: css`
    margin-bottom: ${theme.spacing(5)};
    @container (max-width: ${breakpoints.small.max}px) {
      margin: ${theme.spacing(0, 2, 5, 2)};
    }
  `,
};

export const TagList = ({
  tags,
  channelId,
  callStartedAt,
  callRecordLocationId,
  queryKey,
}: {
  tags: Tag[];
  channelId: string;
  callStartedAt: string;
  callRecordLocationId: string;
  queryKey: string[];
}) => {
  const { t } = useTranslation('calls');
  const alerts = useAlert();
  const { emitEvent } = useCallInsightController({
    source: 'callHistory',
  });

  const { removeTag } = useRecentCallsTagMutation({
    queryKey,
    callStartedAt,
    channelId,
    callRecordLocationId,
    optimisticRemoveTag: optimisticCallRecordsRemoveTag,
    onSuccess: ({ op, updatedTagIds }) => {
      if (op === 'remove') {
        !!updatedTagIds && emitEvent('callTagsExternalSync', { channelId, tagIds: updatedTagIds });
        alerts.success(t('Successfully removed tag.'));
      }
    },
    onFailure: ({ op }) => {
      if (op === 'remove') alerts.error(t('Failed to remove tag.'));
    },
  });
  const representativeTag = tags[0];

  return (
    <>
      <Chip.Tag
        isRemovable
        onRemoveClick={() => {
          removeTag(representativeTag);
        }}
        css={{ width: 'auto' }}
        color={TagsUtils.convertStringToTagColor(representativeTag.color)}
      >
        {representativeTag.name}
      </Chip.Tag>
    </>
  );
};

export const TagColumn = ({
  tags,
  channelId,
  callStartedAt,
  callRecordLocationId,
  queryKey,
  showPanel,
  isEditing,
  setIsEditing,
}: {
  tags: Tag[];
  channelId: string;
  callStartedAt: string;
  callRecordLocationId: string;
  queryKey: string[];
  showPanel: () => void;
  isEditing?: boolean;
  setIsEditing: (enabled: boolean) => void;
}) => {
  const alerts = useAlert();
  const { t } = useTranslation('calls');
  const { emitEvent } = useCallInsightController({
    source: 'callHistory',
  });

  const { applyTag } = useRecentCallsTagMutation({
    queryKey: queryKey,
    channelId: channelId,
    callStartedAt: callStartedAt,
    callRecordLocationId: callRecordLocationId,
    optimisticApplyTag: optimisticCallRecordsApplyTag,
    onSuccess: ({ op, updatedTagIds }) => {
      if (op === 'add') {
        !!updatedTagIds && emitEvent('callTagsExternalSync', { channelId, tagIds: updatedTagIds });
        alerts.success(t('Tag added successfully.'));
      }
    },
    onFailure: ({ op }) => {
      if (op === 'add') alerts.error(t('Failed to add tag.'));
    },
  });

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0px, 1fr) auto auto',
        alignItems: 'center',
        gap: theme.spacing(1),
      }}
    >
      {tags.length > 0 && (
        <TagList
          tags={tags}
          channelId={channelId}
          callStartedAt={callStartedAt}
          callRecordLocationId={callRecordLocationId}
          queryKey={queryKey}
        />
      )}
      {!isEditing && tags.length > 1 && (
        <TextLink
          css={{
            ':hover': {
              textDecoration: 'none',
            },
          }}
          onClick={() => {
            showPanel();
          }}
        >
          +{tags.length - 1}
        </TextLink>
      )}
      {!isEditing && tags.length === 0 && <AddTag existingTagIds={tags.map((tag) => tag.id)} onApplyTag={applyTag} />}
      {isEditing && (
        <RecentCallsTagSelector
          onClose={() => {
            setIsEditing(false);
          }}
          onSelect={() => {
            setIsEditing(false);
          }}
          onApplyTag={applyTag}
          existingTagIds={tags.map((tag) => tag.id)}
        />
      )}
    </div>
  );
};

type TagsCellProps = RecentCallRecord & {
  row: Row<RecentCallRecord>;
  tableInstance: TableInstance<RecentCallRecord>;
  hasAccesstoCallRecording: boolean;
  searchQuery: string;
};

const TagsCell = ({ tags, channelId, locationId, startedAt, row, searchQuery }: TagsCellProps) => {
  const { open: openCallInsightPanel } = useCallHistoryCallInsight();

  let isEditing = false;
  if (row?.state && 'isEditing' in row.state && typeof row.state.isEditing === 'boolean') {
    isEditing = row.state.isEditing;
  }

  if (!channelId || !startedAt || !locationId || !row) return null;

  return (
    <TagColumn
      tags={tags}
      channelId={channelId}
      callStartedAt={startedAt}
      callRecordLocationId={locationId}
      showPanel={() => {
        openCallInsightPanel({ hydratedCall: row.original }, { defaultAccordion: 'callIntel' });
      }}
      queryKey={queryKeys.recentCallLogs(searchQuery).filter(Boolean)}
      isEditing={isEditing}
      setIsEditing={(val) => row.setState({ isEditing: val })}
    />
  );
};

const registerSearch = (data: RecentCallRecord, itemPosition?: number) => {
  if (!data.id || itemPosition === undefined) return;

  handleRegisterClick({
    entity: Entity.ENTITY_CALL,
    entityId: data.id,
    entityPosition: 0,
    itemPosition,
  });
};

export const CallHistoryTableView = ({
  hasVoiceAi,
  isAirEventsUiEnabled,
  isLoading: _isLoading,
  getHistoryActions,
  infiniteQueryProps,
  selectedLocationIds,
  emptyState,
  panelProps: _panelProps,
  searchQuery,
  setPageConfig,
  pageNumber = 1,
  pageSize,
  filterConfig,
  accessConfig,
  dataConfig,
  handlers,
}: {
  hasVoiceAi: boolean;
  isAirEventsUiEnabled: boolean;
  isLoading: boolean;
  getHistoryActions: (props: HistoryActionsGetterProps) => (React.ReactElement | null)[];
  infiniteQueryProps: UseInfiniteQueryResult<RecentCallRecords, unknown>;
  selectedLocationIds: string[];
  emptyState: EmptyStateConfig;
  panelProps: {
    callDetailsQueryKey: string[];
    hasAccessToRecordings: boolean;
    sectionFocus: string;
  };
  searchQuery: string;
  setPageConfig: (input: SetPageConfigInput) => void;
  pageNumber?: number;
  pageSize: number;
  filterConfig: {
    setFilters: (filters: CallRecordsFiltersType) => void;
    filters: CallRecordsFiltersType;
    defaultFilters: CallRecordsFiltersType;
    phoneNumberOptions: Record<string, { name: string; numbers: { value: string; label: string; name: string }[] }>;
    searchHandler: (searchText: string) => void;
    hasFilterChanged: (filters: CallRecordsFiltersType, defaultFilters: CallRecordsFiltersType) => boolean;
    showEnabledFiltersBadgeHandler: MutableRefObject<((showNotification: boolean) => void) | undefined>;
  };
  accessConfig: {
    callIntelLocationPlanMap: Record<string, ProductTier>;
    hasAccesstoCallRecording: boolean;
    hasAccesstoCallRecordingMap: Record<string, boolean>;
    departmentsEnabled: boolean;
    showCallIntelInsights: boolean;
    hasCallIntelPremiumFeatures: boolean;
    showServiceQuality: boolean;
  };
  dataConfig: {
    departmentsByLocation: Record<string, DepartmentsTypes.ListDepartmentTypes['output']>;
    editedCallIds: string[];
    shouldRegisterSearch: boolean;
  };
  handlers: {
    handleExport: () => void;
  };
}) => {
  const { t } = useTranslation('calls');

  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const { getLocationName } = useAppScopeStore();
  const [tableInstance, setTableInstance] = useState<TableInstance<RecentCallRecord[]>>();
  const alert = useAlert();
  const [isCreditEnforcementEnabled] = CustomerLifecycleManagementOrg.useWetag('air-credit-enforcement');

  const {
    setFilters,
    filters,
    defaultFilters,
    phoneNumberOptions,
    searchHandler,
    hasFilterChanged,
    showEnabledFiltersBadgeHandler,
  } = filterConfig;

  const {
    hasAccesstoCallRecording,
    hasAccesstoCallRecordingMap,
    departmentsEnabled,
    showCallIntelInsights,
    hasCallIntelPremiumFeatures,
    showServiceQuality,
    callIntelLocationPlanMap,
  } = accessConfig;
  const isWeaveDemoUser = useCallIntelWeaveDemoUser();

  const { departmentsByLocation, editedCallIds, shouldRegisterSearch } = dataConfig;
  const { handleExport } = handlers;

  const { data: recentCallRecords, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } = infiniteQueryProps;

  const { voicemailData } = useVoicemailDetailsShallowStore('voicemailData');

  const {
    activeCall,
    setCallList,
    open: openCallInsightPanel,
    clearCallList,
    setIsDemoAccount,
  } = useCallHistoryCallInsight();

  const { setTaskBySourceId } = useTaskCitation();

  const { completeHotspotByTarget } = useOnboardingShallowStore('completeHotspotByTarget');

  const getLocationChip = useCallback(
    (locationId: string | undefined) => {
      if (!!locationId) {
        return <Chips.LocationChip>{getLocationName(locationId)}</Chips.LocationChip>;
      }
      return (
        <span>
          <EmptyCell tooltipText={t('No Data')} />{' '}
        </span>
      );
    },
    [getLocationName, t]
  );

  const hasPrivateCommsFlag = getFeatureFlagValue('apply-phone-number-user-access-restriction');

  const openCallPathView = useCallback(
    (call: RecentCallRecord) => {
      openCallInsightPanel({ hydratedCall: call }, { currentScreenView: ScreenType.CALL_PATH_MAPPING });
    },
    [openCallInsightPanel]
  );

  const getCallIntelAccessProps = useCallback(
    (locationId?: string) => {
      const locationPlan = locationId ? callIntelLocationPlanMap[locationId] : undefined;

      if (!locationId) {
        return {};
      }

      if (hasCallIntelPlanAccess(locationPlan)) {
        return {};
      }

      return {
        isCallIntelAccessRestricted: true,
        callIntelAccessRestrictedTooltip: t('Call Intelligence is not available for this location.'),
      };
    },
    [callIntelLocationPlanMap, t]
  );

  const getTaskAccessProps = useCallback(
    (locationId?: string) => {
      const locationPlan = locationId ? callIntelLocationPlanMap[locationId] : undefined;

      if (!locationId) {
        return {};
      }

      if (canUseCallIntelPremiumFeatures(locationPlan)) {
        return {};
      }

      return {
        isCallIntelAccessRestricted: true,
        callIntelAccessRestrictedTooltip: t('Tasks are not available for this location.'),
      };
    },
    [callIntelLocationPlanMap, t]
  );

  const tableData = useMemo(
    () => recentCallRecords?.pages[pageNumber - 1]?.data || [],
    [recentCallRecords, pageNumber]
  );

  const {
    callInsightHotspot,
    contactProfileHotspot,
    isSearching: isOnboardingSearching,
  } = useCallInsightEligibleHotspot({
    tableData,
    currentPage: pageNumber,
    allPages: recentCallRecords?.pages,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    setPageNumber: (page: number) => setPageConfig({ pageNumber: page }),
    openCallInsightPanel,
  });

  const colConfig: TableColumnConfig<RecentCallRecord>[] = useMemo(
    () => [
      {
        Header: t('Contact Name'),
        accessor: (callItem) => callItem,
        cellRenderer: (item) => {
          return <ContactNameCell item={item} showHotspot={item.id === contactProfileHotspot?.id} />;
        },
        disableSortBy: true,
        id: 'contactName',
        mobileLayoutConfig: {
          order: -1,
          spanFullWidth: false,
          hideHeader: true,
          leftShift: 48,
        },
        minWidth: 220,
        sticky: 'left',
      },
      {
        Header: t('Time of Call'),
        accessor: (row) => {
          return dayjs(row.startedAt).format('MMM DD YYYY, hh:mm A');
        },
        disableSortBy: true,
        id: 'dateTime',
        minWidth: 180,
      },
      {
        Header: t('Office User'),
        accessor: (callInfo) => ({
          aiOutcome: callInfo.aiMetadata?.aiOutcome,
          officeUser: PhonesUtils.getOfficeUser(callInfo),
        }),
        cellRenderer: ({ aiOutcome, officeUser }) => {
          if (hasVoiceAi) {
            if (
              aiOutcome === AIOutcome.AI_OUTCOME_END_CALL ||
              aiOutcome === AIOutcome.AI_OUTCOME_FATAL ||
              aiOutcome === AIOutcome.AI_OUTCOME_INCOMPLETE ||
              aiOutcome === AIOutcome.AI_OUTCOME_TEXT_HANDOFF ||
              aiOutcome === AIOutcome.AI_OUTCOME_ROUTE_MISSED ||
              aiOutcome === AIOutcome.AI_OUTCOME_UNKNOWN
            ) {
              return <AiOfficeUser />;
            }

            if (aiOutcome === AIOutcome.AI_OUTCOME_ROUTE_TRANSFERRED) {
              return <AiOfficeUserRouted name={officeUser} />;
            }
          }

          if (!officeUser || officeUser === '--') {
            return <EmptyCell tooltipText={t('No Data')} />;
          }

          return officeUser;
        },
        omit: !hasVoiceAi,
        disableSortBy: true,
        id: 'officeUserAi',
        width: 180,
      },
      {
        Header: (
          <div style={{ display: 'grid', gridAutoFlow: 'column', alignItems: 'center', gap: theme.spacing(1) }}>
            {isAirEventsUiEnabled ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing(1),
                  color: 'var(--fabric-color-neutral-50)',
                }}
              >
                <Icon name='aiweave-assistant' size={16} css={{ color: 'var(--fabric-color-neutral-50)' }} />
                <Text size='medium' css={{ color: 'var(--fabric-color-neutral-50)' }}>
                  {t('AI Receptionist Outcomes')}
                </Text>
              </div>
            ) : (
              <WithTooltip tooltipText={t('AI Call Outcome')}>
                <Icon name='aiweave-assistant' size={16} />
              </WithTooltip>
            )}

            {hasVoiceAi && (isAirEventsUiEnabled ? <AiEventDescriptionModalTrigger /> : <AiOutcomeInfoModalTrigger />)}
          </div>
        ),
        headerLabel: (
          <div
            style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', alignItems: 'center', gap: theme.spacing(0.5) }}
          >
            <Icon name='aiweave-assistant' size={16} />{' '}
            {isAirEventsUiEnabled ? t('AI Receptionist Outcomes') : t('AI Call Outcome')}
          </div>
        ),
        accessor: (row) => ({
          aiOutcome: row.aiMetadata?.aiOutcome,
          events: row.aiMetadata?.aiReceptionistEventTypes,
          row,
        }),
        cellRenderer: ({ aiOutcome, events, row }) => {
          if (isAirEventsUiEnabled) {
            return (
              <AiEventInfoTableCell
                events={events}
                onClick={() => {
                  openCallInsightPanel(
                    { hydratedCall: row },
                    { defaultAccordion: 'aboutThisCall', defaultCallBreakdownTab: 'callActivity' }
                  );
                }}
              />
            );
          }

          return (
            <div css={{ display: 'grid', placeItems: 'center', width: '100%' }}>
              {getAiOutcomeIcon({ useTooltip: true, outcome: aiOutcome })}
            </div>
          );
        },
        disableSortBy: true,
        id: 'aiCallOutcome',
        omit: !hasVoiceAi,
        width: isAirEventsUiEnabled ? undefined : hasVoiceAi ? 74 : 48,
        minWidth: isAirEventsUiEnabled ? 240 : 48,
      },
      {
        Header: (
          <div style={{ display: 'grid', gridAutoFlow: 'column', alignItems: 'center', gap: theme.spacing(1) }}>
            <span>{t('Result')}</span>

            {hasVoiceAi && <ResultInfoModalTrigger />}
          </div>
        ),
        headerLabel: t('Result'),
        accessor: (call) => call,
        cellRenderer: (call) => {
          const { direction, status, hasVoicemail } = call;
          if (direction === 'outbound')
            return (
              <EmptyCell data-pendo-target={'nwx-phones-recentCalls-row-no-status'} tooltipText={t('Outbound call')} />
            );
          const statusChip = PhonesUtils.getStatusChipColor(status);
          const isMissedCall = direction === 'inbound' && status === 'missed';

          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
              <Chip
                onClick={() => openCallPathView(call)}
                css={{ cursor: 'pointer' }}
                isResponsive
                variant={statusChip}
                data-pendo-target={'nwx-phones-recentCalls-row-has-status'}
              >
                {startCase(status)}
              </Chip>
              {isMissedCall && hasVoicemail && <VoicemailButton callRecord={call} />}
            </div>
          );
        },
        disableSortBy: true,
        id: 'result',
        width: 150,
        cellTrackingId: 'callhistory-table-result-column-clicks',
      },
      {
        Header: t('Contact Number'),
        accessor: ({ callerNumber, direction, dialedNumber }) =>
          formatPhoneNumber(direction === 'inbound' ? callerNumber : dialedNumber),
        disableSortBy: true,
        id: 'contactPhone',
        width: 150,
      },
      {
        Header: t('Location'),
        accessor: ({ locationId }) => getLocationChip(locationId),
        id: 'location',
        omit: !(selectedLocationIds.length > 1),
        mobileLayoutConfig: {
          order: 1,
        },
        cellTrackingId: 'nwx-phones-recentCalls-row-location',
      },
      {
        Header: t('Office Number'),
        accessor: ({ callerNumber, direction, dialedNumber }) =>
          formatPhoneNumber(direction === 'outbound' ? callerNumber : dialedNumber),
        disableSortBy: true,
        id: 'officePhone',
        mobileLayoutConfig: {
          order: 1,
        },
        width: 150,
      },
      {
        Header: t('Call Recording'),
        accessor: ({
          channelId,
          locationId,
          recordingPath,
          recordingFilename,
          isRecordingBeingProcessed,
          downloadCallRecordingToken,
        }) => {
          const hasCallRecording = !!recordingPath && !!recordingFilename;
          const isCallRecordingBeingProcessed = !hasCallRecording && isRecordingBeingProcessed;
          return {
            channelId,
            locationId,
            hasCallRecording: hasCallRecording,
            isRecordingBeingProcessed: isCallRecordingBeingProcessed,
            downloadCallRecordingToken,
          };
        },
        cellRenderer: ({
          channelId,
          locationId,
          hasCallRecording,
          isRecordingBeingProcessed,
          downloadCallRecordingToken,
        }) => (
          <>
            {channelId && locationId && hasAccesstoCallRecordingMap[locationId] && hasCallRecording ? (
              <CachedAudioScrubber
                trackingId='nwx-phones-recentCalls-row-callRecording-audioScrubber'
                filePath={`${appConfig.BACKEND_API}/portal/v1/phone/callRecordings/${channelId}?location_id=${locationId}`}
                queryFn={
                  hasPrivateCommsFlag
                    ? async () => {
                        try {
                          const res = await PhoneCallsApi.getCallRecording({
                            channelId,
                            locationId,
                            token: downloadCallRecordingToken,
                          });
                          if (res.signedUrl) {
                            return fetch(res.signedUrl);
                          } else {
                            throw new Error('No signed URL available');
                          }
                        } catch (error) {
                          console.error('Error fetching call recording:', error);
                          throw error;
                        }
                      }
                    : undefined
                }
                mediaId={`call-recording-${channelId}`}
                css={{ maxWidth: '100%', height: 24 }}
              />
            ) : isRecordingBeingProcessed ? (
              <>
                <span css={styles.recordingBeingProcessed}>{t('Processing...')}</span>
                <SpinningLoader
                  css={css`
                    display: block;
                  `}
                  size='small'
                />
              </>
            ) : (
              <span css={styles.noRecordingText}>{'--'}</span>
            )}
          </>
        ),
        disableSortBy: true,
        id: 'callRecording',
        omit: !hasAccesstoCallRecording,
        mobileLayoutConfig: {
          spanFullWidth: true,
          order: 1,
        },
        minWidth: 200,
      },
      {
        Header: t('Phone Number Name'),
        accessor: 'officeNumberName',
        cellRenderer: (officeNumberName) => {
          if (!officeNumberName) return <EmptyCell tooltipText={t('No Data')} />;
          return (
            <div
              css={css`
                max-width: 150px;
              `}
            >
              <Truncated as={Text} size='medium' color='default'>
                {officeNumberName}
              </Truncated>
            </div>
          );
        },
        disableSortBy: true,
        id: 'officeNumberName',
        width: 180,
      },
      {
        Header: t('Office User'),
        accessor: (callInfo) => ({
          officeUser: PhonesUtils.getOfficeUser(callInfo),
        }),
        cellRenderer: ({ officeUser }) => {
          if (!officeUser || officeUser === '--') {
            return <EmptyCell tooltipText={t('No Data')} />;
          }

          return officeUser;
        },
        omit: hasVoiceAi,
        disableSortBy: true,
        id: 'officeUser',
        width: 180,
      },
      {
        Header: t('Device Name'),
        accessor: 'deviceName',
        cellRenderer: (deviceName) => {
          if (!deviceName || deviceName === '--') {
            return <EmptyCell tooltipText={t('No device found')} />;
          }
          return (
            <Truncated as={Text} size='medium' color='default'>
              {deviceName}
            </Truncated>
          );
        },
        disableSortBy: true,
        id: 'deviceName',
        width: 180,
      },
      {
        Header: <TaskColumn.Header showCallIntelPrefix />,
        headerLabel: t('Tasks'),
        accessor: (callInfo) => callInfo,
        cellRenderer: (callInfo) => {
          const { callIntelInsights, direction, status, taskCount } = callInfo;
          return (
            <div css={styles.tagsWrapper}>
              <TaskColumn.Cell
                callInsights={callIntelInsights}
                callRecordStatus={status}
                direction={direction}
                taskCount={taskCount}
                {...getTaskAccessProps(callInfo.locationId)}
                onClick={async () => {
                  if (taskCount === 1) {
                    try {
                      await setTaskBySourceId({
                        sourceId: callIntelInsights.id,
                        source: TaskSource.SOURCE_CALL_INTELLIGENCE,
                      });
                    } catch (error) {
                      alert.error(t('Unable to open task. Please try again.'));
                    }
                  } else {
                    openCallInsightPanel({ hydratedCall: callInfo }, { defaultAccordion: 'tasks' });
                  }
                }}
              />
            </div>
          );
        },
        disableSortBy: true,
        id: 'tasks',
        width: 160,
        omit: !hasCallIntelPremiumFeatures,
      },
      {
        Header: t('Tags'),
        accessor: (item) => item,
        id: 'tags',
        disableSortBy: true,
        cellRenderer: (record, _rowData, row, _1, _2, tbInstance) => {
          return (
            <TagsCell
              {...record}
              row={row}
              tableInstance={tbInstance}
              hasAccesstoCallRecording={hasAccesstoCallRecording}
              searchQuery={searchQuery}
            />
          );
        },
      },
      {
        Header: <ContactTypeColumn.Header showCallIntelPrefix />,
        headerLabel: t('Contact Type'),
        accessor: ({ callIntelInsights, direction, locationId, status }) => ({
          callIntelInsights,
          direction,
          locationId,
          status,
        }),
        cellRenderer: ({ callIntelInsights, locationId, status, direction }) => {
          return (
            <ContactTypeColumn.Cell
              callInsights={callIntelInsights}
              callRecordStatus={status}
              direction={direction}
              {...getCallIntelAccessProps(locationId)}
            />
          );
        },
        disableSortBy: true,
        id: 'contactType',
        width: 170,
        omit: !hasCallIntelPremiumFeatures,
      },
      {
        Header: <SentimentColumn.Header showCallIntelPrefix />,
        headerLabel: t('Customer Sentiment'),
        accessor: ({ callIntelInsights, direction, locationId, status }) => ({
          callIntelInsights,
          direction,
          locationId,
          status,
        }),
        cellRenderer: ({ callIntelInsights, direction, locationId, status }) => {
          return (
            <SentimentColumn.Cell
              callInsights={callIntelInsights}
              callRecordStatus={status}
              direction={direction}
              {...getCallIntelAccessProps(locationId)}
            />
          );
        },
        disableSortBy: true,
        id: 'sentiment',
        width: 215,
        omit: !hasCallIntelPremiumFeatures,
      },
      {
        Header: <ServiceIssueColumn.Header showCallIntelPrefix />,
        headerLabel: t('Service Issues'),
        accessor: (callInfo) => callInfo,
        cellRenderer: (callInfo) => {
          const { callIntelInsights, direction, status } = callInfo;

          return (
            <ServiceIssueColumn.Cell
              callInsights={callIntelInsights}
              callRecordStatus={status}
              direction={direction}
              {...getCallIntelAccessProps(callInfo.locationId)}
              onClick={() => {
                openCallInsightPanel(
                  { hydratedCall: callInfo },
                  {
                    defaultAccordion: 'callIntel',
                    defaultCallIntelTab: 'callAnalysis',
                  }
                );
              }}
            />
          );
        },
        disableSortBy: true,
        id: 'serviceQualityFlags',
        width: 350,
        omit: !hasCallIntelPremiumFeatures || !showServiceQuality,
      },
      {
        Header: <SchedulingOutcomeColumn.Header showCallIntelPrefix />,
        headerLabel: t('Scheduling Outcome'),
        accessor: ({ callIntelInsights, direction, locationId, status }) => ({
          callIntelInsights,
          direction,
          locationId,
          status,
        }),
        cellRenderer: ({ callIntelInsights, direction, locationId, status }) => {
          return (
            <SchedulingOutcomeColumn.Cell
              callInsights={callIntelInsights}
              callRecordStatus={status}
              direction={direction}
              {...getCallIntelAccessProps(locationId)}
            />
          );
        },
        disableSortBy: true,
        id: 'schedulingOutcome',
        width: 220,
        omit: !hasCallIntelPremiumFeatures,
      },
      {
        Header: <CategoryColumn.Header showCallIntelPrefix />,
        headerLabel: t('Category'),
        accessor: ({ callIntelInsights, direction, locationId, status }) => ({
          callIntelInsights,
          direction,
          locationId,
          status,
        }),
        cellRenderer: ({ callIntelInsights, locationId, status, direction }) => {
          return (
            <CategoryColumn.Cell
              callInsights={callIntelInsights}
              callRecordStatus={status}
              direction={direction}
              {...getCallIntelAccessProps(locationId)}
            />
          );
        },
        disableSortBy: true,
        id: 'category',
        width: 160,
        omit: !hasCallIntelPremiumFeatures,
      },
      {
        Header: t('Phone Tree'),
        accessor: (call) => call,
        cellRenderer: (call) => {
          if (!call.phoneTrees || call.phoneTrees.length === 0) {
            return <EmptyCell tooltipText={t('No Data')} />;
          }
          return <PhoneTreePreview onClick={() => openCallPathView(call)} ivrEvents={call.phoneTrees} />;
        },
        disableSortBy: true,
        id: 'phoneTrees',
        width: 220,
        cellTrackingId: 'callhistory-table-phone-tree-column-clicks',
      },
      {
        Header: t('Network Performance'),
        accessor: 'networkPerformance',
        cellRenderer: (networkPerformance) => {
          switch (networkPerformance) {
            case NetworkPerformance.NETWORK_PERFORMANCE_ISSUE_DETECTED: {
              return (
                <Chip variant='warn' css={{ width: 'max-content', maxWidth: 'max-content' }}>
                  {t('Issues Detected')}
                </Chip>
              );
            }
            case NetworkPerformance.NETWORK_PERFORMANCE_GOOD: {
              return (
                <Chip variant='success' css={{ width: 'max-content' }}>
                  {t('Stable')}
                </Chip>
              );
            }
            default:
              return <EmptyCell tooltipText={t('No Data')} />;
          }
        },
        disableSortBy: true,
        id: 'networkPerformance',
        width: 188,
        cellTrackingId: 'callhistory-table-networkperformance',
      },
      {
        Header: t('Department'),
        accessor: ({ departmentId, locationId }) => {
          if (!locationId) return 'Main Line';
          const dept = departmentsByLocation[locationId]?.departments?.find((item) => item.id === departmentId);
          return !!departmentId ? dept?.name : 'Main Line';
        },
        disableSortBy: true,
        id: 'department',
        mobileLayoutConfig: {
          order: 1,
        },
        omit: !departmentsEnabled,
      },
      {
        Header: (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto auto 1fr',
              alignItems: 'center',
              gap: 'var(--fabric-space-gap-xs)',
            }}
          >
            <Icon name='aiweave-assistant' size={16} /> {t('AI Call Credit')}
            {hasVoiceAi && <AiCallCreditInfoModalTrigger />}
          </div>
        ),
        headerLabel: (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              alignItems: 'center',
              gap: 'var(--fabric-space-gap-xs)',
            }}
          >
            <Icon name='aiweave-assistant' size={16} /> {t('AI Call Credit')}
          </div>
        ),
        id: 'aiCallCredit',
        accessor: (callInfo) => {
          if (callInfo.direction === 'outbound') {
            return undefined;
          }

          const aiCallCreditStatus = callInfo.aiCallCreditStatus;

          if (aiCallCreditStatus === 'AI_CALL_CREDIT_STATUS_CHARGED') {
            return 'used';
          } else if (aiCallCreditStatus === 'AI_CALL_CREDIT_STATUS_NOT_CHARGED') {
            return 'notUsed';
          } else if (aiCallCreditStatus === 'AI_CALL_CREDIT_STATUS_OUT_OF_CREDITS') {
            return 'out';
          } else {
            return undefined;
          }
        },
        cellRenderer: (aiCallCreditUsed) => <CreditUsageLabel aiCallCreditUsed={aiCallCreditUsed} />,
        disableSortBy: true,
        omit: !hasVoiceAi || !isCreditEnforcementEnabled,
      },
      {
        Header: '',
        accessor: (callInfo: any) => callInfo,
        cellRenderer: (_, rowData, row, _1, _2, tbInstance) => {
          if (!rowData) {
            return null;
          }

          const addTag = () => {
            if (row) {
              row.setState({ isEditing: true });
            }
          };

          const actions = getHistoryActions({
            rowData,
            addTag,
            index: row?.index ?? 0,
            isInsidePopoverDropdown: true,
          });

          return (
            <>
              <div style={{ width: '100%', display: 'flex', gap: theme.spacing(1), justifyContent: 'center' }}>
                <AdaptoActions
                  iconColor='inherit'
                  maxActions={2}
                  moreActionsTrackingId='phone-portal-weave2.0-calls-icn-tablerowactionmenu'
                  css={css`
                    button {
                      width: 32px;
                    }
                  `}
                  popoverPlacement='left'
                >
                  {row && tbInstance && (
                    <OnboardingHotspot
                      target={HOTSPOT_TARGET.CALL_HISTORY_CALL_INSIGHT_BUTTON}
                      offset={{ right: 0 }}
                      enabled={callInsightHotspot?.id === rowData?.id}
                    >
                      <ViewCallDetailsColumn
                        row={row}
                        showCallIntelInsights={showCallIntelInsights}
                        isEdited={editedCallIds.includes(rowData?.channelId ?? '')}
                        registerClick={() => {
                          if (shouldRegisterSearch) {
                            registerSearch(rowData, row.index);
                          }
                        }}
                      />
                    </OnboardingHotspot>
                  )}
                  {actions}
                </AdaptoActions>
              </div>
            </>
          );
        },
        disableSortBy: true,
        id: 'actions',
        sticky: 'right',
        disableColumnFilter: true,
        width: 100,
        mobileLayoutConfig: {
          order: -1,
          spanFullWidth: false,
          hideHeader: true,
        },
      },
    ],

    [
      t,
      hasVoiceAi,
      isAirEventsUiEnabled,
      selectedLocationIds.length,
      hasAccesstoCallRecording,
      showCallIntelInsights,
      hasCallIntelPremiumFeatures,
      showServiceQuality,
      departmentsEnabled,
      isCreditEnforcementEnabled,
      openCallPathView,
      getLocationChip,
      hasAccesstoCallRecordingMap,
      hasPrivateCommsFlag,
      getTaskAccessProps,
      setTaskBySourceId,
      alert,
      openCallInsightPanel,
      searchQuery,
      getCallIntelAccessProps,
      departmentsByLocation,
      getHistoryActions,
      editedCallIds,
      shouldRegisterSearch,
      callInsightHotspot?.id,
      contactProfileHotspot?.id,
    ]
  );

  useEffect(() => {
    setCallList(
      tableData.map((call) => ({
        hydratedCall: call,
      })),
      {
        trackingContext: { source: 'recent-calls-table' },
      }
    );

    return () => clearCallList();
  }, [clearCallList, setCallList, tableData, tableData.length]);

  useEffect(() => {
    if (!tableInstance) return;

    if (!activeCall?.id) {
      tableInstance.toggleAllRowsSelected(false);
      return;
    }

    tableInstance.toggleAllRowsSelected(false);

    const row = tableInstance.rowsById[activeCall.id];

    if (row && !row.isSelected) {
      row.toggleRowSelected(true);
    }

    // Scroll the active row into view smoothly
    const tableId = tableInstance.tableId;
    const allRowEls = Array.from(document.querySelectorAll<HTMLElement>(`#${tableId} [role="row"]`));
    const idx = tableInstance.rows.findIndex((r) => r.id === activeCall.id);
    if (idx >= 0 && allRowEls[idx]) {
      allRowEls[idx].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [activeCall?.id, tableInstance]);

  useEffect(() => {
    if (showCallIntelInsights && isWeaveDemoUser) {
      setIsDemoAccount(true);
    } else {
      setIsDemoAccount(false);
    }
  }, [isWeaveDemoUser, setIsDemoAccount, showCallIntelInsights]);

  useEffect(() => {
    completeHotspotByTarget(HOTSPOT_TARGET.CALLS_NAVIGATION_MENU);
  }, [completeHotspotByTarget]);

  return (
    <TableObserver
      onTableRender={(tbInstance) => {
        setTableInstance(tbInstance);
      }}
      render={({ ref }) => (
        <Table
          allowMobileView={false}
          uniqueRowId={(rowData, _) => {
            return PhonesUtils.getRecentCallsUniqueKey(rowData);
          }}
          tableInstanceId='recent-calls'
          instanceRef={ref}
          hasResizeColumns
          hasFilterColumns
          emptyStateConfig={emptyState}
          data={tableData}
          customToolbarRender={() => (
            <ToolbarChipFilter
              trackingBaseId='phone-weave2.0-recentCalls'
              showHandledByFilter
              filters={{
                locationIds: filters.locationIds,
                startDate: filters.startDate ?? '',
                endDate: filters.endDate ?? '',
                aiReceptionistHandled: filters.aiReceptionistHandled,
                handledByUserIds: filters.handledByUserIds,
                allOfficeUsers: filters.allOfficeUsers,
              }}
              onChange={(changedfilter) => {
                setFilters({
                  ...filters,
                  locationIds: changedfilter.locationIds,
                  startDate: changedfilter.startDate,
                  endDate: changedfilter.endDate,
                  aiReceptionistHandled: changedfilter.aiReceptionistHandled,
                  handledByUserIds: changedfilter.handledByUserIds,
                  allOfficeUsers: changedfilter.allOfficeUsers,
                });
              }}
            />
          )}
          colConfig={colConfig}
          hasResponsiveColWidths
          isLoading={(isFetching || isFetchingNextPage) && !isOnboardingSearching}
          fullHeight
          isPaginated
          hasGlobalSearch
          globalTrackingId='phone-portal-weave2.0-calls'
          globalSearchConfig={{
            initialValue: filters.phoneNumber || filters.name || '',
            position: 'right',
            searchHandler,
            placeholder: t('Search name or 10-digit number'),
            debounceDelay: 1000,
          }}
          manualFilters
          manualFiltersOptions={{
            initialBadgeState: Object.values(filters).some((filter) =>
              Array.isArray(filter) ? filter.length > 0 : !!filter
            ),
          }}
          manualFiltersRender={(modalProps, setShowNotificationBadge) => {
            // we handle showing the badge in the parent component instead of the tray filter (using this ref)
            showEnabledFiltersBadgeHandler.current = setShowNotificationBadge;

            return (
              modalProps.show && (
                <div css={styles.filtersWrapper}>
                  <RecentCallsTrayFilter
                    hasVoiceAi={hasVoiceAi}
                    hasFilterChanged={(filter) => hasFilterChanged(filter, filters)}
                    filters={filters}
                    resetFilters={() => setFilters(defaultFilters)}
                    modalProps={modalProps}
                    onChange={setFilters}
                    isMulti={selectedLocationIds.length > 1}
                    phoneNumberOptions={phoneNumberOptions}
                  />
                </div>
              )
            );
          }}
          manualPaginationConfig={{
            handleChange: (action: 'next' | 'prev') => {
              if (action === 'next') {
                // Only fetch if we don't have the page loaded yet
                // pageNumber is 1-indexed, so we add 1 to get to the next page, but then remove 1 to get the correct index
                if (hasNextPage && !recentCallRecords?.pages[pageNumber]) {
                  fetchNextPage();
                }
                setPageConfig((prev: PaginationSettings) => ({ pageNumber: prev.pageNumber + 1 }));
              } else {
                setPageConfig((prev: PaginationSettings) => ({ pageNumber: prev.pageNumber - 1 }));
              }
            },
            /**
             * Either we have a next page (that comes after the very last page we have queried)
             * `hasNextPage` is true
             *
             * or we have data for the next page in the `recentCallRecords` object
             * `recentCallRecords?.pages[pageNumber]` is truthy
             *
             * Note that `pageNumber` is 1-indexed, so we add 1 to get to the next page, but then remove 1 to get the correct index
             */
            hasNext: hasNextPage || !!recentCallRecords?.pages[pageNumber],
            hasPrevious: pageNumber > 1,
            page: pageNumber,
            defaultRowsPerPage: pageSize,
            onNumRowsChange: (num) => {
              setPageConfig({ pageNumber: 1, pageSize: num });
            },
            rowsPerPageOptions: [10, 25, 50, 75],
          }}
          tableActions={[
            {
              Icon: DownloadIcon,
              disabled: !!isFetching || !recentCallRecords?.pages[pageNumber - 1]?.data.length,
              label: t('Download Data'),
              trackingId: 'nwx-phones-recentCalls-export-btn',
              onClick: handleExport,
              type: 'icon',
            },
          ]}
          // TODO: Revert the below commit (6985d53c7a86074766d7ff3c3864a81eba3148cb) once this ticket(PE-6405) is done. It will be doing a complete refactor of how the panels are managed and rows are highlighted
          // Voicemail highlight is visual-only to avoid conflicting with call-insights single-row selection
          // (which uses `toggleRowSelected(true)` and drives panel context + scroll).
          styleConfig={{
            rows: (rowData) => {
              const isActiveCallRow = !!activeCall?.id && rowData.id === activeCall.id;
              const voicemailLocationId = voicemailData?.locationIds?.[0];
              const isVoicemailRow =
                !!voicemailData?.voicemailId &&
                rowData.channelId === voicemailData.voicemailId &&
                (!voicemailLocationId || rowData.locationId === voicemailLocationId);

              // Avoid double-highlight: if call insights has an active selection, only highlight voicemail
              // when it matches that same row. Otherwise keep the call-insights selection as the single highlight.
              return isVoicemailRow && (!activeCall?.id || isActiveCallRow)
                ? {
                    background: theme.colors.neutral5,
                  }
                : {};
            },
          }}
        />
      )}
    />
  );
};

/**
 * This component is used solely to observe the table instance and pass the instance to the parent component.
 */
const TableObserver = ({
  onTableRender,
  render,
}: {
  onTableRender?: (tableInstance: TableInstance<RecentCallRecord[]>) => void;
  render: ({ ref }: { ref: MutableRefObject<TableInstance<RecentCallRecord[]>> }) => ReactNode;
}) => {
  const tableInstanceRef = useRef<TableInstance<RecentCallRecord[]>>(
    null as unknown as TableInstance<RecentCallRecord[]>
  );

  // This effect intentionally has no dependencies to ensure it runs after the component has rendered.
  useEffect(() => {
    onTableRender?.(tableInstanceRef.current);
  });

  return render({ ref: tableInstanceRef });
};

const ContactNameCell = ({ item, showHotspot }: { item: RecentCallRecord; showHotspot?: boolean }) => {
  const person = item.person;
  const { t } = useTranslation('calls');
  const { firstName, lastName, legalName } = useGetDisplayName({
    person: {
      firstName: person?.firstName || '',
      lastName: person?.lastName || '',
      preferredName: person?.preferredName,
    },
  });
  const personId = item?.person?.personId;

  return (
    <OnboardingHotspot
      target={HOTSPOT_TARGET.PATIENT_CONTACT_AVATAR}
      offset={{ top: -4, left: 24 }}
      enabled={!!showHotspot && !!personId}
    >
      <ProfileCard
        trackingId={`phone-portal-weave2.0-calls-profilebtn-tablerowaction-viewprofile`}
        firstName={firstName ?? ''}
        lastName={lastName ?? ''}
        shouldUsePotentialName={item?.direction === 'inbound'}
        potentialName={item?.callerName && !isNaN(+item?.callerName) ? '' : item?.callerName}
        defaultName={t('Unknown')}
        locationId={item?.locationId}
        personID={item?.person?.personId}
        title={legalName}
        showIcon={item?.direction === 'outbound'}
      />
    </OnboardingHotspot>
  );
};
